import React from "react";
import { useSubscription } from "../context/SubscriptionContext";
import { useAuth } from "../context/AuthContext";
import { Card, CardBody, Spinner } from "./ui";
import { FaLock, FaCrown } from "react-icons/fa";
import { Link } from "react-router-dom";
import { isFeatureAvailable } from "../utils/featureEntitlements";

const FEATURE_META = {
  flowBuilder: { label: "Flow Builder", description: "Build automated WhatsApp conversation flows with a visual drag-and-drop editor." },
  outreach: { label: "Campaigns", description: "Create outreach campaigns with multi-step WhatsApp sequences across multiple sessions." },
  sendViaExcel: { label: "Send via Excel", description: "Bulk send WhatsApp messages by uploading an Excel or CSV file." },
  teamMembers: { label: "Team Members", description: "Invite team members to collaborate on your WhatsApp workspace." },
  aiFeatures: { label: "AI Features", description: "Use AI-powered responses and knowledge bases for automated conversations." },
  verification: { label: "Number Verification", description: "Verify WhatsApp numbers before sending to reduce failed deliveries." },
  whatsappWidget: { label: "WhatsApp Widget", description: "Embed a WhatsApp chat widget on your website." },
  imageToUrl: { label: "Image / File to URL", description: "Upload images and files to get shareable URLs for WhatsApp messages." },
  cloudApi: { label: "WhatsApp Cloud API", description: "Connect Meta-hosted WhatsApp Business numbers." },
  qrSessions: { label: "WhatsApp QR Sessions", description: "Connect WhatsApp Web numbers with QR pairing." },
};

export default function FeatureGate({ featureKey, children }) {
  const { subscription, loading, error, refreshSubscription } = useSubscription();
  const { role } = useAuth();

  // Admins bypass all gates
  if (role === "admin") return <>{children}</>;

  // Keep an already-authorized feature mounted during background entitlement
  // refreshes. Replacing the page with a spinner here reset in-progress UI
  // state whenever quota changes emitted `subscription:update` — including
  // hiding a freshly generated WhatsApp QR immediately after Connect.
  if (loading && !subscription) {
    return (
      <div className="flex items-center justify-center min-h-[300px]">
        <Spinner size="lg" />
      </div>
    );
  }

  if (error && !subscription) {
    return (
      <div className="page-content flex items-center justify-center min-h-[60vh]">
        <Card className="max-w-md w-full text-center">
          <CardBody className="py-12 px-8">
            <div className="w-14 h-14 rounded-2xl bg-red-100 flex items-center justify-center mx-auto mb-5">
              <FaLock size={20} className="text-red-600" />
            </div>
            <h2 className="text-lg font-bold text-ink-primary mb-2">Plan check temporarily unavailable</h2>
            <p className="text-sm text-ink-muted mb-6 leading-relaxed">
              We could not verify your subscription. Your plan has not been removed, and you do not need to upgrade.
            </p>
            <button type="button" className="btn-primary inline-flex items-center gap-2 px-6" onClick={refreshSubscription}>
              Try again
            </button>
          </CardBody>
        </Card>
      </div>
    );
  }

  const isAvailable = isFeatureAvailable(subscription, featureKey, role);

  if (isAvailable) return <>{children}</>;

  // Feature not available — show upgrade prompt
  const meta = FEATURE_META[featureKey] || { label: featureKey, description: "This feature requires an upgraded plan." };
  return (
    <div className="page-content flex items-center justify-center min-h-[60vh]">
      <Card className="max-w-md w-full text-center">
        <CardBody className="py-12 px-8">
          <div className="w-14 h-14 rounded-2xl bg-amber-100 flex items-center justify-center mx-auto mb-5">
            <FaLock size={20} className="text-amber-600" />
          </div>
          <h2 className="text-lg font-bold text-ink-primary mb-2">
            {meta.label}
          </h2>
          <p className="text-sm text-ink-muted mb-6 leading-relaxed">
            {meta.description}
            <br />
            <span className="text-ink-faint mt-2 block">
              Upgrade your plan to unlock this feature and take your workflow to the next level.
            </span>
          </p>
          <Link
            to="/pricing"
            className="btn-primary inline-flex items-center gap-2 px-6"
          >
            <FaCrown size={12} />
            View Plans
          </Link>
          <p className="text-2xs text-ink-faint mt-4">
            Have questions? <a href="mailto:support@walytic.com" className="underline hover:text-ink-secondary">We're happy to help.</a>
          </p>
        </CardBody>
      </Card>
    </div>
  );
}
